import { ErrorReport, ErrorReportAdapter } from '../error-report';

// Note: This file contains optional syntax highlighting.
// To view it, use VSCode and install the following recommended modules:
// • es6-string-html
// • es6-string-css

/**
 * Configuration options for the module error page.
 */
interface Options {
  /**
   * A custom title
   * @default 'Fatal bootstrap error'
   */
  title?: string;
  /**
   * Whether the error overlay can be closed.
   * @default false
   */
  close?: boolean;
}

/**
 * Module error page CSS.
 * This will be embedded on the page when the module error page is created.
 */
const css = /*css*/`
.nom-module-error {
  position: fixed;
  inset: 0px;
  background: rgba(255, 255, 255, 0.85);
  font-family: 'Helvetica Neue', 'Open Sans', sans-serif;
  font-size: 16px;
  line-height: 1.1em;
  padding: 16px;
  overflow: auto;
  z-index: 1;
}

.nom-module-error h1 {
  font-size: 20px;
  line-height: 32px;
  margin: 0;
}

/* Note that we could be using slot elements, but support is relatively recent. */
.nom-module-error .slot {
  display: none;
}

.nom-module-error .close {
  background: transparent;
  border-width: 0;
  font-size: 20px;
}

.nom-module-error header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.nom-module-error dt {
  font-weight: bold;
  margin: 12px 0 8px;
}

.nom-module-error dd {
  margin-inline-start: 20px;
}

.nom-module-error .actions {
  margin: 8px 0;
}

.nom-module-error details {
  margin: 8px 0;
}

.nom-module-error summary {
  padding: 8px 0;
}

.nom-module-error pre {
  padding: 8px 0px;
  overflow-x: auto;
}
`;

/**
 * A module error page for displaying a fatal error bootstrapping an AppModule or similar.
 *
 * Use this for circumstances where Angular won't be available, possibly because it's crashed.
 */
export class ModuleErrorPage {
  public readonly adapter = new ErrorReportAdapter();
  public report: ErrorReport;

  /**
   * The display element, if it's been created.
   */
  protected display: HTMLElement | null = null;

  /**
   * Construct a new module error page.
   *
   * @param error The error to display
   */
  constructor(error: any) {
    if (error instanceof ErrorReport) {
      this.report = error;
    } else {
      this.report = new ErrorReport(error);
    }
  }

  /**
   * Create the page and embed it in the view.
   *
   * @param options Optional configuration
   */
  public create(options?: Options): void {
    const styleId = 'nom-module-error-style';

    if (!document.getElementById(styleId)) {
      const style = this.createStyle(styleId);
      document.head.appendChild(style);
    }

    this.display = this.createDisplayElement(options);
    document.body.appendChild(this.display);
  }

  /**
   * Remove the display element from the view.
   */
  public remove(): void {
    this.display?.remove();
  }

  protected createStyle(styleId: string): HTMLStyleElement {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = css;
    return style;
  }

  /**
   * Create and display the page.
   *
   * @param options Optional configuration
   */
  protected createDisplayElement(options?: Options): HTMLElement {
    const displayTemplate = document.createElement('template');
    const title = options?.title || 'Fatal bootstrap error';
    displayTemplate.innerHTML = /*html*/`
      <section class="nom-module-error">
        <header>
          <h1>${this.sanitize(title)}</h1>
          <span class="slot slot-close-button"></span>
        </header>
        <dl>
          <dt>Error</dt>
          <dd>${this.displayValue(this.report.error)}</dd>
          <dt>Message</dt>
          <dd>${this.displayValue(this.report.message)}</dd>
          <dt>Constructor</dt>
          <dd>${this.displayValue(this.report.constructorName)}</dd>
          <dt>typeof</dt>
          <dd>${this.displayValue(this.report.typeof)}</dd>
          <dt>Stack trace</dt>
          <dd>${this.displayValue(this.report.stack ? 'Yes' : 'None')}</dd>
          <dt>Timestamp</dt>
          <dd>${this.displayValue(this.report.timestamp?.toLocaleString())}</dd>
        </dl>
        <p>
          <button class="copy-report" type="button">Copy to clipboard</button>
        </p>
        <span class="slot slot-stack-trace"></span>
        <details>
          <summary>Clipboard fallback</summary>
          <input type="text" class="copy-report-fallback">
        </details>
      </section>
    `;
    const display = this.compileTemplate<HTMLElement>(displayTemplate);

    display.querySelector('.copy-report')?.addEventListener('click', () => {
      this.clipboard(this.adapter.stringify(this.report));
    });

    const fallback = display.querySelector<HTMLInputElement>('.copy-report-fallback');
    if (fallback) {
      fallback.value = this.adapter.stringify(this.report);
      fallback.addEventListener('click', () => {
        fallback?.select();
      });
    }

    if (options?.close) {
      const closeTemplate = document.createElement('template');
      closeTemplate.innerHTML = /*html*/`
        <button type="button" class="close">&times;</button>
      `;
      const close = this.compileTemplate<HTMLButtonElement>(closeTemplate);
      close.addEventListener('click', () => display.remove());

      const slot = display.querySelector('.slot-close-button');
      slot?.replaceWith(close);
    }

    if (this.report.stack) {
      const stack = this.report.stack;

      const stackTraceTemplate = document.createElement('template');
      stackTraceTemplate.innerHTML = /*html*/`
        <details>
          <summary>Stack trace</summary>
          <button type="button">Copy stack trace</button>
          <pre><code>${this.sanitize(stack)}</code></pre>
        </details>
      `;
      const stackTrace = this.compileTemplate<HTMLButtonElement>(stackTraceTemplate);
      stackTrace.querySelector('button')?.addEventListener('click', () => {
        this.clipboard(stack);
      });

      const slot = display.querySelector('.slot-stack-trace');
      slot?.replaceWith(stackTrace);
    }

    return display;
  }

  /**
   * Copy a value to the clipboard
   *
   * @param str The value to copy
   */
  protected clipboard(str: string): void {
    window.navigator.clipboard.writeText(str);
  }

  /**
   * Compile a template and emit its first child element.
   * This means the relatively simple templates we make here must have a single root element.
   *
   * @param template The template to compile
   * @returns The compiled template's first root element
   */
  protected compileTemplate<T extends HTMLElement>(template: HTMLTemplateElement): T {
    return template.content.firstElementChild?.cloneNode(true) as T;
  }

  /**
   * Display a value safely in the error summary.
   * This mainly exists to handle undefined values that wouldn't display well otherwise.
   *
   * @param value The value to display
   * @returns A value sanitized for the error summary
   */
  protected displayValue(value: any): string {
    if (value === null || typeof value === 'undefined') {
      return `<i>${value}</i>`;
    } else if (value === '') {
      return `<i>(empty string)</i>`;
    } else {
      return this.sanitize(value);
    }
  }

  /**
   * Sanitize a value for display in the template.
   * Mostly this means escaping `<`/`>` symbols so they won't be interpreted as HTML elements.
   *
   * @param value The value to sanitize
   * @returns The sanitized value
   */
  protected sanitize(value: any): string {
    return `${value}`
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
